'use client';

import { useEffect, useLayoutEffect, type PropsWithChildren } from 'react';

import { useEvent } from 'react-use';

import { DEPLOY_ENV, WIDGET_VERSION } from '@/common/constants';
import { useConfig } from '@/components/providers/config-provider';
import { useSafePostHog } from '@/hooks/use-safe-posthog';
import { setInitialComposerText } from '@/lib/composer-text-tracking';
import {
  isSetComposerTextMessage,
  isWidgetEventMessage,
  POST_MESSAGE_TYPES,
  WINDOW_EVENTS,
} from '@/lib/constants/post-message-types';
import { isInIframe } from '@/lib/utils/dom';
import { readBootstrapCookie } from '@/lib/utils/posthog-bootstrap-client';

const ALIAS_SESSION_FLAG = 'bazak-aliased';

interface WidgetAnalyticsProviderProps extends PropsWithChildren {
  isWidget?: boolean;
  widgetVersion?: string;
  resolvedParentContext?: Record<string, unknown>;
}

function getParentUrlProperties(): {
  parentUrl?: string;
  parentHost?: string;
  parentPathname?: string;
} {
  if (typeof window === 'undefined') return {};

  try {
    const searchParams = new URLSearchParams(window.location.search);
    const parentUrl = searchParams.get('parentUrl');
    if (!parentUrl) return {};

    const url = new URL(parentUrl);
    return {
      parentUrl,
      parentHost: url.host,
      parentPathname: url.pathname,
    };
  } catch {
    return {};
  }
}

export function WidgetAnalyticsProvider({
  children,
  isWidget = false,
  widgetVersion,
  resolvedParentContext,
}: WidgetAnalyticsProviderProps) {
  const posthog = useSafePostHog();
  const { tenantKey, analytics } = useConfig();
  const version = widgetVersion || WIDGET_VERSION;
  const enableSessionRecording = analytics?.enableSessionRecording !== false;

  useLayoutEffect(() => {
    const parentProperties = isWidget ? getParentUrlProperties() : {};
    posthog?.register({
      isWidget,
      widgetVersion: version,
      tenantKey,
      tenant: tenantKey,
      environment: DEPLOY_ENV,
      ...parentProperties,
    });

    if (!enableSessionRecording) {
      posthog?.set_config({ disable_session_recording: true });
      posthog?.stopSessionRecording();
    }
  }, [posthog, isWidget, version, tenantKey, enableSessionRecording]);

  useEffect(() => {
    if (!isWidget || !isInIframe()) return;
    const distinctId = posthog?.get_distinct_id?.() || undefined;
    window.parent.postMessage(
      { type: POST_MESSAGE_TYPES.IFRAME_READY, resolvedContext: resolvedParentContext, distinctId },
      '*',
    );
  }, [isWidget, posthog, resolvedParentContext]);

  // Merge orphan server-bootstrapped UUIDs (e.g. `widget_loaded` captured by
  // `/api/embed.js` from cookies that didn't persist) onto posthog's identity.
  useEffect(() => {
    if (!isWidget || !isInIframe() || !posthog) return;
    try {
      if (sessionStorage.getItem(ALIAS_SESSION_FLAG)) return;
      const bootstrap = readBootstrapCookie();
      const current = posthog.get_distinct_id?.();
      if (bootstrap?.distinctID && current && bootstrap.distinctID !== current) {
        posthog.alias(bootstrap.distinctID);
      }
      sessionStorage.setItem(ALIAS_SESSION_FLAG, '1');
    } catch {
      // sessionStorage may be unavailable (private mode).
    }
  }, [isWidget, posthog]);

  useEvent('message', (event: MessageEvent) => {
    if (!isWidget) return;
    // Every message this handler acts on comes from the embedding page. One check here covers
    // every branch below, including ones added later.
    if (event.source !== window.parent) return;

    if (isSetComposerTextMessage(event.data)) {
      const text = event.data.text;
      const submit = event.data.submit === true;
      const newConversation = event.data.newConversation === true;
      const focus = event.data.focus !== false;
      // Track initial text for analytics comparison when user submits
      setInitialComposerText(text);
      // Store for components that mount later (e.g., Composer on initial load)
      window.__pendingComposerText = text;
      window.__pendingComposerSubmit = submit;
      window.__pendingComposerNewConversation = newConversation;
      window.__pendingComposerFocus = focus;
      window.dispatchEvent(
        new CustomEvent(WINDOW_EVENTS.SET_COMPOSER_TEXT, {
          detail: { text, submit, newConversation, focus },
        }),
      );
      return;
    }

    if (isWidgetEventMessage(event.data)) {
      const { action, trigger, focus } = event.data;
      if (action === 'opened') {
        if (enableSessionRecording) {
          posthog?.startSessionRecording();
        }
        window.dispatchEvent(
          new CustomEvent(WINDOW_EVENTS.OPENED, { detail: { trigger, focus: focus !== false } }),
        );
      } else if (action === 'closed') {
        posthog?.stopSessionRecording();
        window.dispatchEvent(new CustomEvent(WINDOW_EVENTS.CLOSED, { detail: {} }));
      } else if (action === 'setTrigger') {
        window.dispatchEvent(new CustomEvent(WINDOW_EVENTS.SET_TRIGGER, { detail: { trigger } }));
      }
    }
  });

  return <>{children}</>;
}
